/**
 * The pre-authentication surface — design/09 §3, G2 §2.6.
 *
 * Two screens reach this app before a session exists: `LoginGate` when there is no
 * session, and `AuthCallback` while Keycloak's authorization code is being exchanged.
 * Both used to render a bare `EmptyState` or a bare `Skeleton` on the page plane, which
 * satisfied §2.6's "designed for every surface" in the narrow sense — the states existed
 * — and failed §3's in the broad one: a planner "should recognise the product as Bata's
 * within one second", and the first screen in the product showed an inbox icon.
 *
 * This is the one place in the application where the brand can be spent without any
 * argument about proximity to data. There is no grid here, no variance figure, no
 * semantic colour: §3's "deliberately absent from the data surface" has nothing to
 * restrain because there is no data surface.
 *
 * THE LAYOUT, and why it is two panes rather than an overlay. Text over photography is a
 * contrast problem that has to be solved twice — once per theme — and is usually solved
 * with a scrim, which design/09 §9 rules out in spirit ("no automatic inversion, no
 * filter hacks"; both modes are designed, not computed). Putting the photograph in its
 * own pane makes the contrast question disappear: every glyph sits on `surface-raised`
 * in both themes, and the photograph is whole rather than half-hidden under a card.
 *
 * It also buys the rendition ladder its top tier. The photograph is 50vw from `md`
 * (1024px) up, so a 1440px viewport renders it at 720 CSS px and the 1440 rendition is
 * its exact 2× — which a 1500px source could not have provided for a full-bleed
 * 100vw hero. See ADR 0086 §4.
 */

import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BataWordmark } from './BataWordmark';
import { BrandHero, pickHeroPhotograph } from './BrandHero';
import { SUPPORTED_LOCALES } from '../../lib/format';
import { APP_VERSION, COMMIT_SHA } from '../../lib/build-info';
import { FOCUS_RING } from '../../primitives/focus';
import { i18next } from '../../i18n';

export interface SignInSurfaceProps {
  /** The panel: a designed state, not a screen. Sits on `surface-raised` in both themes. */
  readonly children: React.ReactNode;
  /** Names the landmark for a screen reader. A catalogue key, never prose. */
  readonly labelKey: string;
}

export function SignInSurface({ children, labelKey }: SignInSurfaceProps): React.JSX.Element {
  const { t } = useTranslation();
  // Chosen once per mount (ADR 9017), so the photograph does not change while the planner reads.
  const [photograph] = useState(() => pickHeroPhotograph());
  return (
    <main
      aria-label={t(labelKey)}
      className="bg-surface-page grid min-h-dvh grid-cols-1 md:grid-cols-2"
    >
      {/*
        The photograph is first in the DOM so that on a narrow viewport it is the band
        above the panel without an order override. Above `md` it moves to the inline end
        — `order`, not a physical property, so a right-to-left locale mirrors it with the
        rest of the layout (G6).

        `aspect-[3/2]` below `md` matches the rendition's own aspect exactly, so the band
        shows the whole crop rather than a cover-crop of it. Above `md` the pane is as
        tall as the viewport and `object-cover` takes the centre band.
      */}
      <figure className="bg-surface-sunken relative aspect-[3/2] overflow-hidden md:order-2 md:aspect-auto">
        <BrandHero photograph={photograph} />
        {/*
          The brand's four columns, ruled over the photograph (designer audit 8.2, brief
          part 1 §7.2): FOUR rules in `hero-rule`, white at 14% — one at the inline start of
          each column, so the three between the columns sit exactly where the brief draws
          them and the fourth marks the pane's own edge. Decoration, so out of the
          accessibility tree; logical borders, so a right-to-left locale mirrors them.
        */}
        <div aria-hidden="true" className="pointer-events-none absolute inset-0 grid grid-cols-4">
          <span className="border-hero-rule border-s" />
          <span className="border-hero-rule border-s" />
          <span className="border-hero-rule border-s" />
          <span className="border-hero-rule border-s" />
        </div>
        {/*
          ADR 9017 - the product's name, in text, in the pane's block-end inline-START corner.

          Brief part 1 §7.2 (DEC-95) settles its look: the numeric face at 10px, uppercase,
          `hero-caption` (#d9d5cc), 12px in from the side and 10px up from the foot, and NO
          chip — it replaced the opaque `surface-raised` chip this sat on before. The
          photograph is always black and white and the value does not change with the
          theme, so the pair is the same in both. `start-*`, not `left-*`, so a
          right-to-left locale mirrors it with the layout (G6). `lang` because the name is
          the Colombian storefront's Spanish whatever the interface locale is, and a screen
          reader should say it that way.
        */}
        <figcaption
          lang="es-CO"
          className="font-numeric text-hero-caption absolute bottom-[10px] start-[12px] max-w-[calc(100%-24px)] truncate text-[10px] uppercase leading-none tracking-[0.06em]"
        >
          {photograph.productName}
        </figcaption>
      </figure>

      {/*
        ADR 9004 - the pane IS the page, not a card floating on it.

        It was a bordered card centred in the pane; the approved redesign gives the column
        its full height, with the mark at the top, the panel at the optical centre and the
        build and locale at the foot. The card is gone rather than restyled: a bordered box
        inside a pane that is already a box is a second container saying nothing.
      */}
      <div className="flex flex-col justify-between gap-8 p-6 md:order-1 md:p-12">
        <BataWordmark height={26} />

        <div className="flex w-full max-w-lg flex-col gap-6">{children}</div>

        {/*
          The foot. Both halves are here because this is the one screen a planner can read
          BEFORE they have a session: the locale control, so somebody who does not read
          English can change it before signing in rather than after, and the build, so a
          screenshot of a sign-in failure can be traced to a commit (G7.5) without asking
          the person who took it to log in first.
        */}
        <div className="flex flex-wrap items-center justify-between gap-4">
          {/*
            Three words, not a native select (designer audit F3): every language this build
            speaks is in view before sign-in, each named in itself (`lang`), the one in use in
            semibold and pressed.
          */}
          <div role="group" aria-label={t('locale.label')} className="text-label flex flex-wrap items-center gap-2">
            {SUPPORTED_LOCALES.map((locale, index) => {
              const active = i18next.language === locale;
              return (
                <Fragment key={locale}>
                  {index > 0 && <span aria-hidden="true" className="text-ink-muted">·</span>}
                  <button
                    type="button"
                    lang={locale}
                    aria-pressed={active}
                    onClick={() => { void i18next.changeLanguage(locale); }}
                    className={`rounded-control underline-offset-4 hover:underline ${active ? 'text-ink-primary font-semibold' : 'text-ink-secondary'} ${FOCUS_RING}`}
                  >
                    {t(`locale.short.${locale}`)}
                  </button>
                </Fragment>
              );
            })}
          </div>
          <span className="text-caption text-ink-secondary font-numeric tabular-nums">
            {t('app.versionFooter', { version: APP_VERSION, commit: COMMIT_SHA })}
          </span>
        </div>
      </div>
    </main>
  );
}

/**
 * The panel body shared by both pre-authentication states.
 *
 * §4.2 gives `type-display` to "page title only, one per screen" — this is that title,
 * and it is the only Antonio on the surface. The body is `type-body`; §4.2's rule that
 * Antonio never carries body copy is not negotiable even here.
 */
export interface SignInPanelProps {
  readonly titleKey: string;
  readonly bodyKey: string;
  /**
   * A failure rather than a state of the world. Adds `role="alert"`, the same thing
   * `EmptyState` does for its `error` variant — and nothing else. §12.6's "colour never
   * alone" is satisfied by there being no colour here at all: the failure is carried by
   * the sentence, which is what a screen reader gets too.
   */
  readonly alert?: boolean;
  readonly children?: React.ReactNode;
}

export function SignInPanel({ titleKey, bodyKey, alert = false, children }: SignInPanelProps): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <>
      <div className="flex flex-col gap-2" {...(alert ? { role: 'alert' } : {})}>
        {/*
          Brief part 1 §7.3 (DEC-95): Antonio 34 uppercase over a DM Sans 13 grey line. A fixed
          34px rather than `text-display`'s clamp, which is 34 only at one viewport width; the
          sign-in surface carries no data, so ADR 9006's display register is not asked to grow
          a step for it.
        */}
        <h1 className="text-ink-primary font-display text-[34px] font-bold uppercase leading-none">{t(titleKey)}</h1>
        <p className="text-ink-secondary max-w-prose text-[13px] leading-5">{t(bodyKey)}</p>
      </div>
      {children}
    </>
  );
}
